import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import download from '@/assets/download.svg';
import glossaryIcon from '@/assets/glossary.svg';
import { ALIGN_TYPE, TextBlock } from '@/components/Elements';
import { ContentLayout } from '@/components/Layout';

import { useGlossary } from '../hooks/useGlossary';
import { downloadTemplateCsv } from '../utils/csvTemplate';

import styles from './Glossary.module.scss';
import { GlossaryLangPanel } from './GlossaryLangPanel';
import { GlossaryList } from './GlossaryList';
import { GlossaryUploadDialog } from './GlossaryUploadDialog';

/**
 * 用語集ページのコンテンツ。
 * ヘッダー・空状態・一覧・アップロードダイアログを組み合わせる。
 * ロジックは `useGlossary` に委譲する。
 */
const GlossaryView = () => {
  const { t } = useTranslation();

  const {
    entries,
    isUploadOpen,
    setIsUploadOpen,
    languages,
    currentLang,
    page,
    setPage,
    pageSize,
    totalPages,
    filteredCount,
    pageEntries,
    handleTabChange,
    handlePageSizeChange,
    handleRegistered,
  } = useGlossary();

  /**
   * アップロードダイアログを開く。
   */
  const handleUploadOpen = useCallback(
    () => setIsUploadOpen(true),
    [setIsUploadOpen],
  );

  /**
   * アップロードダイアログを閉じる。
   */
  const handleUploadClose = useCallback(
    () => setIsUploadOpen(false),
    [setIsUploadOpen],
  );

  /**
   * テンプレート CSV をダウンロードする。
   * ヘッダーは UI 言語に合わせてローカライズする。
   */
  const handleDownloadTemplate = useCallback(() => {
    downloadTemplateCsv([
      t('glossary.columnLanguage'),
      t('glossary.columnPhrase'),
      t('glossary.columnSoundsLike'),
    ]);
  }, [t]);

  /**
   * テンプレートダウンロードボタンと CSV アップロードボタンのペア。
   * ヘッダー行と空状態の両方で使用する。
   */
  const actionButtons = useMemo(
    () => (
      <>
        <button
          type="button"
          className={styles['template-btn']}
          onClick={handleDownloadTemplate}
        >
          <img src={download} alt="" className={styles['template-btn-icon']} />
          {t('glossary.downloadTemplate')}
        </button>
        <button
          type="button"
          className={styles['upload-btn']}
          onClick={handleUploadOpen}
        >
          {t('glossary.uploadCsv')}
        </button>
      </>
    ),
    [handleDownloadTemplate, handleUploadOpen, t],
  );

  return (
    <div className={styles.page}>
      <div className={styles.title}>
        <TextBlock text={t('glossary.title')} align={ALIGN_TYPE.center} />
      </div>

      {entries.length > 0 && (
        <div className={styles['header-actions']}>{actionButtons}</div>
      )}

      <div className={styles.content}>
        {entries.length === 0 ? (
          <div className={styles['empty-state']}>
            <img src={glossaryIcon} alt="" className={styles['empty-icon']} />
            <p className={styles['empty-title']}>{t('glossary.noEntries')}</p>
            <p className={styles['empty-desc']}>{t('glossary.emptyDescription')}</p>

            <GlossaryLangPanel />

            <div className={styles['empty-actions']}>{actionButtons}</div>
          </div>
        ) : (
          <GlossaryList
            languages={languages}
            currentLang={currentLang}
            pageEntries={pageEntries}
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            filteredCount={filteredCount}
            onTabChange={handleTabChange}
            onPageSizeChange={handlePageSizeChange}
            onPageChange={setPage}
          />
        )}
      </div>

      <GlossaryUploadDialog
        isOpen={isUploadOpen}
        onClose={handleUploadClose}
        onRegistered={handleRegistered}
      />
    </div>
  );
};

/**
 * 用語集画面。
 */
export const Glossary = React.memo(() => (
  <ContentLayout>
    <GlossaryView />
  </ContentLayout>
));
